import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface Transporter {
  id: string;
  name: string;
  latitude?: number;
  longitude?: number;
  service_radius_miles?: number;
  city: string;
  state_code: string;
}

interface TransportMapProps {
  transporters: Transporter[];
  selectedId?: string;
  onMarkerClick?: (id: string) => void;
  mapboxToken: string;
  stateCode?: string;
}

// State center coordinates (approximate)
const STATE_CENTERS: Record<string, [number, number]> = {
  SC: [-80.9, 33.8],
  NC: [-79.8, 35.6],
  GA: [-83.5, 32.9],
  FL: [-81.5, 27.6],
  VA: [-78.6, 37.4],
  TN: [-86.0, 35.9],
  AL: [-86.8, 32.8],
  // Add more as needed
};

export const TransportMap = ({ 
  transporters, 
  selectedId, 
  onMarkerClick, 
  mapboxToken,
  stateCode = 'SC'
}: TransportMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapContainer.current || !mapboxToken) return;

    mapboxgl.accessToken = mapboxToken;
    
    const center = STATE_CENTERS[stateCode] || STATE_CENTERS.SC;
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: center,
      zoom: 7,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Handle Mapbox edge cases gracefully (e.g., Invalid LngLat on mouseout)
    map.current.on('error', (e) => {
      if (e.error?.message?.includes('Invalid LngLat')) {
        return; // Silently ignore known edge case
      }
      console.error('Mapbox error:', e);
    });

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    return () => {
      map.current?.remove();
    };
  }, [mapboxToken, stateCode]);

  // Update markers when transporters change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Clear existing circles
    if (map.current.getSource('radius-circles')) {
      map.current.removeLayer('radius-circles-fill');
      map.current.removeLayer('radius-circles-stroke');
      map.current.removeSource('radius-circles');
    }

    const validTransporters = transporters.filter(t => t.latitude && t.longitude);
    
    if (validTransporters.length === 0) return;

    // Add markers
    validTransporters.forEach((transporter) => {
      const el = document.createElement('div');
      el.className = 'transport-marker';
      el.style.cssText = `
        width: 32px;
        height: 32px;
        background: ${selectedId === transporter.id ? 'hsl(var(--primary))' : 'hsl(var(--secondary))'};
        border: 3px solid white;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s;
      `;
      el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><rect width="16" height="11" x="4" y="10" rx="2"/><path d="M4 15h4V10"/><path d="M8 6v4"/><path d="M12 6v4"/><path d="M16 6v4"/><path d="M4 19a2 2 0 1 0 4 0"/><path d="M16 19a2 2 0 1 0 4 0"/></svg>`;
      
      el.addEventListener('mouseenter', () => {
        el.style.transform = 'scale(1.2)';
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = 'scale(1)';
      });

      const marker = new mapboxgl.Marker(el)
        .setLngLat([transporter.longitude!, transporter.latitude!])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div style="padding: 8px;">
              <strong>${transporter.name}</strong><br/>
              <span style="color: #666;">${transporter.city}, ${transporter.state_code}</span><br/>
              ${transporter.service_radius_miles ? `<span style="color: #888; font-size: 12px;">${transporter.service_radius_miles} mile radius</span>` : ''}
            </div>
          `)
        )
        .addTo(map.current!);

      el.addEventListener('click', () => {
        onMarkerClick?.(transporter.id);
      });

      markersRef.current.push(marker);
    });

    // Add radius circles as a GeoJSON source
    const circleFeatures = validTransporters
      .filter(t => t.service_radius_miles)
      .map(t => createCircle([t.longitude!, t.latitude!], t.service_radius_miles! * 1.60934)); // Convert miles to km

    if (circleFeatures.length > 0) {
      map.current.addSource('radius-circles', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: circleFeatures
        }
      });

      map.current.addLayer({
        id: 'radius-circles-fill',
        type: 'fill',
        source: 'radius-circles',
        paint: {
          'fill-color': 'hsl(var(--primary))',
          'fill-opacity': 0.1
        }
      });

      map.current.addLayer({
        id: 'radius-circles-stroke',
        type: 'line',
        source: 'radius-circles',
        paint: {
          'line-color': 'hsl(var(--primary))',
          'line-width': 1,
          'line-opacity': 0.5
        }
      });
    }

    // Fit bounds to show all transporters
    if (validTransporters.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      validTransporters.forEach(t => {
        bounds.extend([t.longitude!, t.latitude!]);
      });
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 10 });
    } else if (validTransporters.length === 1) {
      map.current.flyTo({
        center: [validTransporters[0].longitude!, validTransporters[0].latitude!],
        zoom: 9
      });
    }
  }, [transporters, selectedId, mapLoaded, onMarkerClick]);

  return (
    <div className="relative w-full h-full min-h-[400px]">
      <div ref={mapContainer} className="absolute inset-0 rounded-lg" />
      {!mapboxToken && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
          <p className="text-muted-foreground">Map configuration required</p>
        </div>
      )}
    </div>
  );
};

// Helper function to create a circle polygon
function createCircle(center: [number, number], radiusKm: number, points = 64) {
  const coords = [];
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = radiusKm * Math.cos(angle);
    const dy = radiusKm * Math.sin(angle);
    const lat = center[1] + (dy / 111.32);
    const lng = center[0] + (dx / (111.32 * Math.cos(center[1] * Math.PI / 180)));
    coords.push([lng, lat]);
  }
  coords.push(coords[0]); // Close the polygon
  
  return {
    type: 'Feature' as const,
    geometry: {
      type: 'Polygon' as const,
      coordinates: [coords]
    },
    properties: {}
  };
}
